import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MobileLayout from "../../components/MobileLayout";
import api from "../../utils/api";
import { formatIndianCurrency } from "../../utils/formatters";
import toast, { Toaster } from "react-hot-toast";
import {
  Wallet,
  TrendingUp,
  Users,
  Download,
  ArrowUpRight,
  Send,
  ArrowRight,
  X,
  AlertCircle,
} from "lucide-react";

const Funds = () => {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferData, setTransferData] = useState({
    fromBalance: "earnings",
    amount: "",
  });
  const [loading, setLoading] = useState(true);
  const [transferring, setTransferring] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [walletRes, txnRes] = await Promise.all([
        api.get("/wallets/me"),
        api.get("/transactions"),
      ]);
      console.log("Wallet Data:", walletRes.data, "Transactions Data:", txnRes.data);
      setWallet(walletRes.data);
      setTransactions(txnRes.data);
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch data:", err);
      setLoading(false);
    }
  };

  const handleTransfer = async () => {
    setError("");

    // Convert amount to float
    const amount = parseFloat(transferData.amount);

    // Frontend validations
    if (isNaN(amount) || amount <= 0) {
      setError("Please enter a valid amount greater than 0");
      return;
    }

    // Check max available balance
    const maxAmount =
      transferData.fromBalance === "earnings"
        ? wallet?.earningsBalance || 0
        : wallet?.networkBalance || 0;

    if (amount > maxAmount) {
      setError(
        `Insufficient ${
          transferData.fromBalance === "earnings"
            ? "Revenue Balance"
            : "Network Balance"
        }. Available: ${formatIndianCurrency(maxAmount)}`
      );
      return;
    }

    setTransferring(true);

    try {
      // Send transfer request with proper types
      const response = await api.post("/wallets/transfer", {
        fromBalance: transferData.fromBalance,
        toBalance: "capital",
        amount: amount, // number, not string
      });

      toast.success(response.data?.message || "Transfer successful!");
      setShowTransfer(false);
      setTransferData({ fromBalance: "earnings", amount: "" });
      setError("");

      // Refresh wallet and transactions
      await fetchData();
    } catch (err) {
      console.error("Transfer error:", err);

      // Handle backend validation errors safely
      let errorMsg = "Transfer failed. Please try again.";

      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;

        if (Array.isArray(detail)) {
          errorMsg = detail.map((d) => d.msg).join(", ");
        } else if (typeof detail === "string") {
          errorMsg = detail;
        }
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      }

      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setTransferring(false);
    }
  };

  const filteredTxns = transactions.filter((txn) => {
    if (activeTab === "all") return true;
    return txn.type === activeTab;
  });

  if (loading) {
    return (
      <MobileLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout>
      <Toaster position="top-right" />
      <div className="p-4 space-y-4 pb-24">
        <h1 className="text-2xl font-heading font-bold text-gray-900">Funds</h1>

        {/* Total Balance Card */}
        <div className="bg-gradient-to-br from-primary to-primary-dark rounded-2xl p-6 text-white shadow-lg">
          <p className="text-sm opacity-90 mb-2">Total Balance</p>
          <p className="text-3xl font-mono font-bold mb-4">
            {formatIndianCurrency(wallet?.totalBalance || 0)}
          </p>
        </div>

        {/* Balance Cards */}
        <div className="space-y-3">
          {/* Capital Balance */}
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-primary-light rounded-lg">
                  <Wallet className="w-5 h-5 text-primary" />
                </div>
                <span className="text-sm font-semibold text-gray-700">
                  Production Fund
                </span>
              </div>
              <p className="text-xl font-mono font-bold text-primary">
                {formatIndianCurrency(wallet?.capitalBalance || 0)}
              </p>
            </div>
            <p className="text-xs text-gray-600">
              Your invested capital for production
            </p>
          </div>

          {/* Revenue Balance */}
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-success-light rounded-lg">
                  <TrendingUp className="w-5 h-5 text-success" />
                </div>
                <span className="text-sm font-semibold text-gray-700">
                  Revenue Balance
                </span>
              </div>
              <p className="text-xl font-mono font-bold text-success">
                {formatIndianCurrency(wallet?.earningsBalance || 0)}
              </p>
            </div>
            <p className="text-xs text-gray-600 mb-2">
              Profit from production cycles
            </p>
            {wallet?.earningsBalance > 0 && (
              <button
                onClick={() => {
                  setShowTransfer(true);
                  setTransferData({ fromBalance: "earnings", amount: "" });
                  setError("");
                }}
                className="text-xs text-primary font-semibold flex items-center gap-1 hover:text-primary-dark transition"
              >
                Transfer to Production Fund <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Network Balance */}
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-warning-light rounded-lg">
                  <Users className="w-5 h-5 text-warning" />
                </div>
                <span className="text-sm font-semibold text-gray-700">
                  Network Balance
                </span>
              </div>
              <p className="text-xl font-mono font-bold text-warning">
                {formatIndianCurrency(wallet?.networkBalance || 0)}
              </p>
            </div>
            <p className="text-xs text-gray-600 mb-2">
              Network commissions & referral rewards
            </p>
            {wallet?.networkBalance > 0 && (
              <button
                onClick={() => {
                  setShowTransfer(true);
                  setTransferData({ fromBalance: "network", amount: "" });
                  setError("");
                }}
                className="text-xs text-primary font-semibold flex items-center gap-1 hover:text-primary-dark transition"
              >
                Transfer to Production Fund <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* Transfer Modal */}
        {showTransfer && (
          <div
            className="fixed inset-0 bg-black/50 flex items-end z-50"
            onClick={() => setShowTransfer(false)}
          >
            <div
              className="bg-white rounded-t-3xl p-6 w-full max-w-lg mx-auto shadow-2xl animate-slide-up max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg text-gray-900">
                  Transfer to Production Fund
                </h3>
                <button
                  onClick={() => setShowTransfer(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition"
                  disabled={transferring}
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transfer From
                  </label>
                  <select
                    value={transferData.fromBalance}
                    onChange={(e) => {
                      setTransferData({
                        ...transferData,
                        fromBalance: e.target.value,
                        amount: "",
                      });
                      setError("");
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition"
                    disabled={transferring}
                  >
                    <option value="earnings">
                      Revenue Balance (
                      {formatIndianCurrency(wallet?.earningsBalance || 0)})
                    </option>
                    <option value="network">
                      Network Balance (
                      {formatIndianCurrency(wallet?.networkBalance || 0)})
                    </option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                      ₹
                    </span>
                    <input
                      type="number"
                      placeholder="0.00"
                      value={transferData.amount}
                      onChange={(e) =>
                        setTransferData({
                          ...transferData,
                          amount: e.target.value,
                        })
                      }
                      max={
                        transferData.fromBalance === "earnings"
                          ? wallet?.earningsBalance
                          : wallet?.networkBalance
                      }
                      min="0"
                      step="0.01"
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition"
                      disabled={transferring}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Available:{" "}
                    {formatIndianCurrency(
                      transferData.fromBalance === "earnings"
                        ? wallet?.earningsBalance || 0
                        : wallet?.networkBalance || 0
                    )}
                  </p>
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-2 sticky bottom-0 bg-white py-16">
                  <button
                    onClick={() => setShowTransfer(false)}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200 transition disabled:opacity-50"
                    disabled={transferring}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleTransfer}
                    className="flex-1 bg-primary text-white py-3 rounded-lg font-semibold hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={transferring}
                  >
                    {transferring ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Processing...
                      </span>
                    ) : (
                      "Transfer"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => navigate("/deposits")}
            className="bg-green-600 text-white rounded-xl p-4 flex flex-col items-center gap-2 shadow-sm hover:bg-green-700 active:scale-95 transition"
          >
            <Download className="w-6 h-6" />
            <span className="text-xs font-semibold text-center">
              Start Production Cycle
            </span>
          </button>

          <button
            onClick={() => navigate("/withdrawals")}
            className="bg-blue-600 text-white rounded-xl p-4 flex flex-col items-center gap-2 shadow-sm hover:bg-blue-700 active:scale-95 transition"
          >
            <ArrowUpRight className="w-6 h-6" />
            <span className="text-xs font-semibold text-center">
              Withdraw Profit
            </span>
          </button>

          <button
            onClick={() => {
              setShowTransfer(true);
              setTransferData({ fromBalance: "network", amount: "" });
              setError("");
            }}
            className="bg-orange-500 text-white rounded-xl p-4 flex flex-col items-center gap-2 shadow-sm hover:bg-orange-600 active:scale-95 transition"
          >
            <Send className="w-6 h-6" />
            <span className="text-xs font-semibold text-center">
              Transfer to Production Fund
            </span>
          </button>
        </div>

        {/* Transactions */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-3">
              Transaction History
            </h3>
            <div className="flex gap-2">
              {["all", "capital"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-lg text-xs font-medium transition ${
                    activeTab === tab
                      ? "bg-primary text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {tab === "all" ? "All" : "Capital"}
                </button>
              ))}
            </div>
          </div>

          <div className="p-4">
            {filteredTxns.length > 0 ? (
              <div className="space-y-3">
                {filteredTxns.slice(0, 10).map((txn) => (
                  <div
                    key={txn.id}
                    className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-sm text-gray-900 capitalize">
                          {txn.referenceType}
                        </p>
                        {/* Show status badge if it exists in meta */}
                        {txn.meta?.status && (
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              txn.meta.status === "pending"
                                ? "bg-yellow-100 text-yellow-700"
                                : txn.meta.status === "active" || txn.meta.status === "completed"
                                ? "bg-green-100 text-green-700"
                                : txn.meta.status === "rejected"
                                ? "bg-red-100 text-red-700"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {txn.meta.status}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600">
                        {new Date(txn.createdAt).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p
                        className={`font-mono font-semibold text-sm ${
                          txn.direction === "credit"
                            ? "text-success"
                            : "text-error"
                        }`}
                      >
                        {txn.direction === "credit" ? "+" : "-"}
                        {formatIndianCurrency(txn.amount)}
                      </p>
                      <span className="text-xs text-gray-500 capitalize">
                        {txn.type}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-3">
                  <Wallet className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-900 mb-1">
                  No transactions yet
                </p>
                <p className="text-xs text-gray-600">
                  Your transaction history will appear here
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </MobileLayout>
  );
};

export default Funds;